import { ethers } from "ethers";
import useSWR from "swr";

import { getServerUrl } from "config/backend";

import { RebateDistribution, RebateDistributionType } from "../types";

type ClaimsResponseItem = {
  id: string;
  account: string;
  marketAddresses?: string[];
  tokenAddresses?: string[];
  amounts?: string[];
  timestamp?: number;
  transaction?: {
    hash?: string;
  };
};

type ClaimsResponse = {
  items?: ClaimsResponseItem[];
};

const AFFILIATE_REWARD_EVENTS = ["AffiliateRewardClaimed"];
const LOOKBACK_SECONDS = 60 * 60 * 24 * 30;
const MAX_REWARD_ROWS = 120;

function toBigInt(value: string | bigint | number | undefined) {
  if (value === undefined || value === null) return 0n;
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return Number.isFinite(value) ? BigInt(Math.trunc(value)) : 0n;
  if (value === "") return 0n;
  try {
    return BigInt(value);
  } catch (_error) {
    return 0n;
  }
}

function normalizeAddress(address?: string | null) {
  if (!address || !ethers.isAddress(address)) return undefined;
  return ethers.getAddress(address);
}

function normalizeAddressArray(addresses?: string[]) {
  if (!addresses?.length) return [];
  return addresses.reduce((acc, address) => {
    const normalized = normalizeAddress(address);
    if (normalized) acc.push(normalized);
    return acc;
  }, [] as string[]);
}

export function useAffiliateDistributions(chainId: number, account?: string | null, enabled = false) {
  const normalizedAccount = account?.toLowerCase();

  const { data, isLoading } = useSWR<RebateDistribution[]>(
    enabled && chainId && normalizedAccount ? [chainId, normalizedAccount, "affiliateDistributions"] : null,
    async () => {
      const query = new URLSearchParams();
      query.set("page", "1");
      query.set("pageSize", String(MAX_REWARD_ROWS));
      query.set("maxItems", String(MAX_REWARD_ROWS));
      // query.set("fromTimestamp", String(Math.floor(Date.now() / 1000) - LOOKBACK_SECONDS));
      AFFILIATE_REWARD_EVENTS.forEach((eventName) => query.append("eventName", eventName));

      const queryString = query.toString();

      const url = getServerUrl(chainId, `/accounts/${normalizedAccount}/claims/new?${queryString}`);
      const response = await fetch(url);
      const json = (await response.json()) as ClaimsResponse;
      const items = json.items || [];

      return items.flatMap((item, index) => {
        const receiver = normalizeAddress(item.account);
        const transactionHash = item.transaction?.hash ?? "";
        if (!receiver || !transactionHash) return [];

        const amounts = (item.amounts || []).map((amount) => toBigInt(parseInt(amount)));

        return [
          {
            typeId: RebateDistributionType.Claim,
            receiver,
            markets: normalizeAddressArray(item.marketAddresses || []),
            tokens: normalizeAddressArray(item.tokenAddresses || []),
            amounts,
            amountsInUsd: amounts.map(() => 0n),
            timestamp: Number(item.timestamp ?? 0),
            transactionHash,
            id: item.id || `${transactionHash}-${index}`,
          },
        ];
      });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  );

  return {
    affiliateDistributions: data || [],
    isLoading,
  };
}
